import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView,
  ActivityIndicator, Image, useWindowDimensions, Modal, Pressable, Animated, Easing,
} from 'react-native';
import { doc, onSnapshot, updateDoc, collection } from 'firebase/firestore';
import { auth, db } from '../firebase/firebase';
import {
  ChatSession, ChatMessage,
  watchActiveSessions, watchClosedSessions, watchMessages,
  markMessagesAsRead, sendAgentMessage, closeChatSession, msOf,
} from '../query/chatService';
import RestaurantOrderMapModal from './RestaurantOrderMapModal';

const C = {
  accent: '#7C3AED', accentLight: '#F3E8FF',
  text: '#111827', sub: '#6B7280', muted: '#9CA3AF',
  border: '#E2E8F0', bg: '#F9FAFB', card: '#fff',
  amber: '#D97706', amberBg: '#FEF3C7',
  slate: '#64748B', slateBg: '#F1F5F9',
  blue: '#2563EB', blueBg: '#EFF6FF', blueBorder: '#BFDBFE',
  danger: '#DC2626',
};

type TabKey = 'open' | 'mine' | 'closed';

function fmtTime(ts: any): string {
  const ms = msOf(ts);
  if (!ms) return '';
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

const initials = (name?: string) =>
  (name || 'U').trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('') || 'U';

const StatusPill = ({ status }: { status: string }) => {
  const map: Record<string, { bg: string; fg: string }> = {
    waiting: { bg: C.amberBg, fg: C.amber },
    active: { bg: C.accentLight, fg: C.accent },
    closed: { bg: C.slateBg, fg: C.slate },
  };
  const s = map[status] || map.closed;
  return (
    <View style={{ backgroundColor: s.bg, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2 }}>
      <Text style={{ color: s.fg, fontSize: 10, fontWeight: '700' }}>{(status || '').toUpperCase()}</Text>
    </View>
  );
};

// ── Order detail panel constants ──
const ORDER_COLLECTION = 'zapee_restaurant_orders';
const PARTNER_COLLECTION = 'zapee_delivery_partners';
// Canonical status flow from the customer app
const ORDER_STATUS_FLOW = [
  'PENDING', 'CONFIRMED', 'PREPARING', 'READY',
  'PICKED_UP', 'OUT_FOR_DELIVERY', 'DELIVERED', 'CANCELLED',
];
const ORDER_STATUS_COLOR: Record<string, string> = {
  DELIVERED: '#10b981', CANCELLED: '#ef4444',
  PENDING: '#f59e0b', CONFIRMED: '#3b82f6',
  PREPARING: '#8b5cf6', READY: '#14b8a6',
  PICKED_UP: '#8b5cf6', OUT_FOR_DELIVERY: '#0ea5e9',
};

interface ROrderDoc {
  id?: string;
  status?: string;
  totalPrice?: number;
  customerPaid?: number;
  distance?: number;          // delivery distance saved on the order (km)
  createdAt?: any;
  orderNumber?: string[];
  deliveryPartnerId?: string;
  deliveryPartnerStatus?: string;
  charges?: { deliveryCharge?: number; packing?: number; platformFee?: number; surge?: number };
  payment?: { method?: string; status?: string };
  orderItems?: Array<{ productName?: string; name?: string; quantity?: number; count?: number; price?: number; unitPrice?: number; isVeg?: boolean }>;
  userLocation?: { name?: string; address?: string; contact?: string; secondaryContact?: string; latitude?: number; longitude?: number };
  deliveryLocation?: { name?: string; address?: string; contact?: string; latitude?: number; longitude?: number };
  sellerLocation?: { name?: string; address?: string; contact?: string; latitude?: number; longitude?: number };
}

interface PartnerDoc {
  docId: string; partnerId?: string; name?: string; phone?: string; email?: string;
  currentOrderId?: string; available?: boolean; active?: boolean;
  currentLocation?: { latitude?: number; longitude?: number };
}
const riderMatches = (r: PartnerDoc, id?: string) => !!id && (r.partnerId === id || r.docId === id);

// Haversine distance (km) between two lat/lng points; null if either is missing.
const distanceKm = (
  a?: { latitude?: number; longitude?: number },
  b?: { latitude?: number; longitude?: number },
): number | null => {
  if (!a?.latitude || !a?.longitude || !b?.latitude || !b?.longitude) return null;
  const R = 6371, toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude), dLon = toRad(b.longitude - a.longitude);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

function fmtOrderDate(ts: any): string {
  if (!ts) return 'N/A';
  try {
    const d = ts?.toDate ? ts.toDate() : (typeof ts === 'number' ? new Date(ts) : new Date(String(ts)));
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return 'N/A'; }
}

const OrderStatusPill = ({ status }: { status?: string }) => {
  const color = ORDER_STATUS_COLOR[(status || '').toUpperCase()] ?? '#6B7280';
  return (
    <View style={[od.statusPill, { backgroundColor: color + '18', borderColor: color + '40' }]}>
      <View style={[od.statusDot, { backgroundColor: color }]} />
      <Text style={[od.statusPillTxt, { color }]}>{status || 'N/A'}</Text>
    </View>
  );
};

export default function SupportChat() {
  const { width } = useWindowDimensions();
  const isMobile = width < 820;

  const myUid = auth.currentUser?.uid || '';

  const [active, setActive] = useState<ChatSession[]>([]);
  const [closed, setClosed] = useState<ChatSession[]>([]);
  const [tab, setTab] = useState<TabKey>('open');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ChatSession | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const feedRef = useRef<ScrollView>(null);

  // ── Order detail + actions panel ──
  const [orderOpen, setOrderOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [orderDoc, setOrderDoc] = useState<ROrderDoc | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderBusy, setOrderBusy] = useState(false);
  const [partners, setPartners] = useState<PartnerDoc[]>([]);
  const [dcDraft, setDcDraft] = useState('');
  const [partnerPick, setPartnerPick] = useState('');
  const [orderToast, setOrderToast] = useState<string | null>(null);

  const orderW = Math.min(isMobile ? width : 480, width);
  const orderX = useRef(new Animated.Value(orderW)).current;

  const flashToast = (msg: string) => {
    setOrderToast(msg);
    setTimeout(() => setOrderToast(null), 2200);
  };

  const closeOrder = () => {
    Animated.timing(orderX, { toValue: orderW, duration: 200, easing: Easing.in(Easing.cubic), useNativeDriver: true })
      .start(() => setOrderOpen(false));
  };

  const openOrder = () => {
    if (!selected?.orderId) return;
    setOrderOpen(true);
  };

  // Animate the drawer in whenever it opens
  useEffect(() => {
    if (orderOpen) {
      orderX.setValue(orderW);
      Animated.timing(orderX, { toValue: 0, duration: 260, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [orderOpen, orderW, orderX]);

  // Live order subscription while the panel is open
  useEffect(() => {
    const orderId = selected?.orderId;
    if (!orderOpen || !orderId) { setOrderDoc(null); return; }
    setOrderLoading(true);
    const unsub = onSnapshot(doc(db, ORDER_COLLECTION, orderId), snap => {
      setOrderDoc(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) }) : null);
      setOrderLoading(false);
    }, () => setOrderLoading(false));
    return () => unsub();
  }, [orderOpen, selected?.orderId]);

  // Keep the delivery-charge input synced to the live doc
  useEffect(() => {
    setDcDraft(orderDoc?.charges?.deliveryCharge != null ? String(orderDoc.charges.deliveryCharge) : '');
  }, [orderDoc?.charges?.deliveryCharge]);

  // Close the panel if the conversation changes
  useEffect(() => {
    setOrderOpen(false);
    setOrderToast(null);
  }, [selected?.id]);

  // Load delivery partners while the panel is open
  useEffect(() => {
    if (!orderOpen) return;
    const unsub = onSnapshot(collection(db, PARTNER_COLLECTION), snap => {
      setPartners(snap.docs.map(d => ({ docId: d.id, ...(d.data() as any) })) as PartnerDoc[]);
    }, () => { /* keep prior list */ });
    return () => unsub();
  }, [orderOpen]);

  const orderId = selected?.orderId || '';

  const writeOrder = async (data: Record<string, any>, successMsg: string) => {
    if (!orderId || orderBusy) return;
    setOrderBusy(true);
    try {
      await updateDoc(doc(db, ORDER_COLLECTION, orderId), data);
      flashToast(successMsg);
    } catch (e) {
      flashToast('Action failed. Please try again.');
    } finally {
      setOrderBusy(false);
    }
  };

  const setOrderStatus = (status: string) => writeOrder({ status }, `Status → ${status}`);
  const cancelOrder = () => {
    if (!window.confirm('Cancel this order?')) return;
    writeOrder({ status: 'CANCELLED' }, 'Order cancelled');
  };
  const saveDeliveryCharge = () => {
    const n = Number(dcDraft);
    if (isNaN(n)) { flashToast('Enter a valid number'); return; }
    writeOrder({ 'charges.deliveryCharge': n }, 'Delivery charge updated');
  };
  const assignPartner = () => {
    if (!partnerPick) { flashToast('Pick a partner first'); return; }
    writeOrder({ deliveryPartnerId: partnerPick }, 'Partner assigned');
  };

  const assignedPartner = useMemo(
    () => partners.find(p => riderMatches(p, orderDoc?.deliveryPartnerId)) || null,
    [partners, orderDoc?.deliveryPartnerId],
  );

  useEffect(() => {
    const u1 = watchActiveSessions(setActive);
    const u2 = watchClosedSessions(setClosed);
    return () => { u1 && u1(); u2 && u2(); };
  }, []);

  // Keep the selected session fresh as snapshots update
  useEffect(() => {
    if (!selected) return;
    const all = [...active, ...closed];
    const fresh = all.find(s => s.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [active, closed]);

  useEffect(() => {
    if (!selected) { setMessages([]); return; }
    setMsgLoading(true);
    const unsub = watchMessages(selected.id, m => {
      setMessages(m);
      setMsgLoading(false);
      markMessagesAsRead(selected.id, m);
      setTimeout(() => feedRef.current?.scrollToEnd({ animated: true }), 60);
    });
    return () => unsub && unsub();
  }, [selected?.id]);

  const sessions = useMemo(() => {
    let list: ChatSession[] = [];
    if (tab === 'open') list = active.filter(s => s.status === 'waiting');
    else if (tab === 'mine') list = active.filter(s => s.status === 'active' && s.assignedAgent === myUid);
    else list = closed.filter(s => s.assignedAgent === myUid);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(s =>
        (s.userName || '').toLowerCase().includes(q) ||
        (s.userEmail || '').toLowerCase().includes(q) ||
        (s.orderCode || '').toLowerCase().includes(q));
    }
    return list;
  }, [tab, active, closed, search, myUid]);

  const openCount = active.filter(s => s.status === 'waiting').length;

  const onSend = async () => {
    if (!selected || !draft.trim() || sending) return;
    const text = draft.trim();
    setDraft('');
    setSending(true);
    try { await sendAgentMessage(selected.id, text, selected); }
    catch (e) { setDraft(text); }
    finally { setSending(false); }
  };

  const onClose = async () => {
    if (!selected) return;
    if (!window.confirm('Close this support chat?')) return;
    try { await closeChatSession(selected.id); } catch { /* ignore */ }
  };

  // ── Sessions list ──
  const SessionsPane = (
    <View style={[st.listPane, !isMobile && { flexGrow: 0, flexShrink: 0, flexBasis: '30%', borderRightWidth: 1, borderRightColor: C.border }]}>
      <View style={st.tabsRow}>
        {([['open', `Open${openCount ? ` (${openCount})` : ''}`], ['mine', 'My Chats'], ['closed', 'Closed']] as [TabKey, string][]).map(([k, lbl]) => (
          <TouchableOpacity key={k} style={[st.tab, tab === k && st.tabActive]} onPress={() => setTab(k)}>
            <Text style={[st.tabTxt, tab === k && st.tabTxtActive]}>{lbl}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <View style={st.searchBox}>
        <Text style={{ marginRight: 6 }}>🔍</Text>
        <TextInput
          style={st.searchInput} value={search} onChangeText={setSearch}
          placeholder="Search name, email, order…" placeholderTextColor={C.muted}
        />
        {search ? <TouchableOpacity onPress={() => setSearch('')}><Text style={{ color: C.muted }}>✕</Text></TouchableOpacity> : null}
      </View>
      <ScrollView contentContainerStyle={{ padding: 10, gap: 8 }}>
        {sessions.length === 0 ? (
          <View style={st.empty}><Text style={{ fontSize: 30 }}>💬</Text><Text style={st.emptyTxt}>No chats here</Text></View>
        ) : sessions.map(s => {
          const waiting = s.status === 'waiting';
          return (
            <TouchableOpacity
              key={s.id}
              style={[st.sessionCard, waiting && st.sessionCardWaiting, selected?.id === s.id && st.sessionCardSelected]}
              onPress={() => setSelected(s)}
            >
              <View style={st.sessionRow}>
                <View style={[st.avatar, { backgroundColor: waiting ? C.amberBg : C.accentLight }]}>
                  <Text style={{ color: waiting ? C.amber : C.accent, fontWeight: '800', fontSize: 13 }}>{initials(s.userName)}</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <View style={st.sessionTop}>
                    <Text style={st.sessionName} numberOfLines={1}>{s.userName || 'Customer'}</Text>
                    <Text style={st.sessionTime}>{fmtTime(s.lastMessageAt)}</Text>
                  </View>
                  <View style={st.sessionMeta}>
                    <StatusPill status={s.status} />
                    {s.orderCode ? (
                      <View style={st.orderBadge}><Text style={st.orderBadgeTxt}>ORDER #{s.orderCode}</Text></View>
                    ) : null}
                    {waiting ? <View style={st.dot} /> : null}
                  </View>
                  <Text style={[st.sessionPreview, waiting && { color: C.text, fontWeight: '600' }]} numberOfLines={1}>
                    {s.lastMessage || 'No messages yet'}
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  // ── Chat room ──
  const RoomPane = (
    <View style={st.roomPane}>
      {!selected ? (
        <View style={st.roomEmpty}>
          <Text style={{ fontSize: 40 }}>💬</Text>
          <Text style={st.roomEmptyTxt}>Select a conversation</Text>
        </View>
      ) : (
        <>
          <View style={st.roomHeader}>
            {isMobile && (
              <TouchableOpacity onPress={() => setSelected(null)} style={st.backBtn}><Text style={st.backTxt}>‹</Text></TouchableOpacity>
            )}
            <View style={{ flex: 1 }}>
              <Text style={st.roomName} numberOfLines={1}>{selected.userName || 'Customer'}</Text>
              {selected.userEmail ? <Text style={st.roomEmail} numberOfLines={1}>{selected.userEmail}</Text> : null}
            </View>
            {selected.status !== 'closed' && (
              <TouchableOpacity style={st.closeBtn} onPress={onClose}><Text style={st.closeBtnTxt}>Close</Text></TouchableOpacity>
            )}
          </View>

          {selected.orderCode ? (
            <TouchableOpacity
              style={st.orderBanner}
              activeOpacity={selected.orderId ? 0.7 : 1}
              onPress={openOrder}
              disabled={!selected.orderId}
            >
              <Text style={{ color: C.blue, flex: 1 }}>🧾  Support for Order #{selected.orderCode}</Text>
              {selected.orderId ? <Text style={{ color: C.blue, fontWeight: '700', fontSize: 12 }}>View order  ›</Text> : null}
            </TouchableOpacity>
          ) : null}

          {msgLoading ? (
            <View style={st.roomEmpty}><ActivityIndicator color={C.accent} /></View>
          ) : (
            <ScrollView ref={feedRef} style={{ flex: 1 }} contentContainerStyle={{ padding: 14, gap: 10 }}
              onContentSizeChange={() => feedRef.current?.scrollToEnd({ animated: false })}>
              {messages.map(m => <Bubble key={m.id} m={m} />)}
            </ScrollView>
          )}

          {selected.status === 'closed' ? (
            <View style={st.closedBar}><Text style={st.closedTxt}>This support session has been closed.</Text></View>
          ) : (
            <View style={st.inputBar}>
              <TextInput
                style={st.input} value={draft} onChangeText={setDraft}
                placeholder="Type your message…" placeholderTextColor={C.muted}
                multiline onSubmitEditing={onSend} blurOnSubmit={false}
              />
              <TouchableOpacity style={[st.sendBtn, (!draft.trim() || sending) && { opacity: 0.5 }]} onPress={onSend} disabled={!draft.trim() || sending}>
                {sending ? <ActivityIndicator color="#fff" size="small" /> : <Text style={st.sendTxt}>➤</Text>}
              </TouchableOpacity>
            </View>
          )}
        </>
      )}
    </View>
  );

  // ── Order detail + actions drawer ──
  const money = (n: any) => `₹${(Number(n) || 0).toFixed(2)}`;
  const orderItems = orderDoc?.orderItems || [];
  const fullOrderNo = orderDoc?.orderNumber?.[0] || orderId || selected?.orderCode || '—';

  const OrderDrawer = (
    <Modal visible={orderOpen} animationType="fade" transparent onRequestClose={closeOrder}>
      <View style={od.root}>
        <Pressable style={od.backdrop} onPress={closeOrder} />
        <Animated.View style={[od.drawer, { width: orderW, transform: [{ translateX: orderX }] }]}>
          <View style={od.header}>
            <TouchableOpacity style={od.closeBtn} onPress={closeOrder}><Text style={od.closeTxt}>✕</Text></TouchableOpacity>
            <Text style={od.title} numberOfLines={1}>Order #{fullOrderNo}</Text>
            <View style={{ width: 32 }} />
          </View>

          {orderLoading && !orderDoc ? (
            <View style={od.loadingWrap}><ActivityIndicator color={C.accent} /></View>
          ) : !orderDoc ? (
            <View style={od.loadingWrap}><Text style={od.empty}>Order not found.</Text></View>
          ) : (
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
              <View style={od.topRow}>
                <OrderStatusPill status={orderDoc.status} />
              </View>

              {/* ── Actions ── */}
              <Text style={od.sec}>Change Status</Text>
              <View style={od.chipWrap}>
                {ORDER_STATUS_FLOW.map(s => {
                  const on = (orderDoc.status || '').toUpperCase() === s;
                  const color = ORDER_STATUS_COLOR[s] ?? C.accent;
                  return (
                    <TouchableOpacity
                      key={s}
                      style={[od.chip, on && { backgroundColor: color, borderColor: color }]}
                      onPress={() => setOrderStatus(s)}
                      disabled={orderBusy}
                    >
                      <Text style={[od.chipTxt, on && { color: '#fff' }]}>{s}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              <Text style={od.sec}>Edit Delivery Charge</Text>
              <View style={od.row2}>
                <TextInput
                  style={od.input}
                  value={dcDraft}
                  onChangeText={setDcDraft}
                  keyboardType="decimal-pad"
                  placeholder="0"
                  placeholderTextColor={C.muted}
                />
                <TouchableOpacity style={od.saveBtn} onPress={saveDeliveryCharge} disabled={orderBusy}>
                  <Text style={od.saveTxt}>Save</Text>
                </TouchableOpacity>
              </View>

              <Text style={od.sec}>Assign Delivery Partner</Text>
              <View style={od.kv}><Text style={od.k}>Current</Text><Text style={od.v}>{assignedPartner?.name || orderDoc.deliveryPartnerId || '— none —'}</Text></View>
              <View style={od.row2}>
                <select
                  value={partnerPick}
                  onChange={(e: any) => setPartnerPick(e.target.value)}
                  style={orderSelectStyle}
                >
                  <option value="">Select a partner…</option>
                  {partners.map(p => {
                    const val = p.partnerId || p.docId;
                    const base = p.name || p.phone || p.email || val;
                    const dist = distanceKm(p.currentLocation, orderDoc.sellerLocation);
                    const busy = !!p.currentOrderId && p.currentOrderId !== orderDoc.id;
                    const parts = [base];
                    if (dist != null) parts.push(`${dist.toFixed(1)}km away`);
                    parts.push(busy ? '⛔ on another order' : '✓ free');
                    return <option key={p.docId} value={val}>{parts.join(' · ')}</option>;
                  })}
                </select>
                <TouchableOpacity style={od.saveBtn} onPress={assignPartner} disabled={orderBusy || !partnerPick}>
                  <Text style={od.saveTxt}>Assign</Text>
                </TouchableOpacity>
              </View>

              {/* ── Read-only details ── */}
              <TouchableOpacity style={od.mapBtn} onPress={() => setMapOpen(true)}>
                <Text style={od.mapTxt}>📍 Track on map — pickup · drop{orderDoc.deliveryPartnerId ? ' · live rider' : ''}</Text>
              </TouchableOpacity>

              <Text style={od.sec}>Order Info</Text>
              <View style={od.kv}><Text style={od.k}>Placed</Text><Text style={od.v}>{fmtOrderDate(orderDoc.createdAt)}</Text></View>
              {orderDoc.distance != null ? <View style={od.kv}><Text style={od.k}>Distance</Text><Text style={od.v}>{Number(orderDoc.distance).toFixed(2)} km</Text></View> : null}
              <View style={od.kv}><Text style={od.k}>Payment</Text><Text style={od.v}>{orderDoc.payment?.method || '—'}{orderDoc.payment?.status ? ` · ${orderDoc.payment.status}` : ''}</Text></View>

              <Text style={od.sec}>Restaurant</Text>
              <View style={od.kv}><Text style={od.k}>Name</Text><Text style={od.v}>{orderDoc.sellerLocation?.name || '—'}</Text></View>
              {orderDoc.sellerLocation?.address ? <View style={od.kv}><Text style={od.k}>Address</Text><Text style={od.v}>{orderDoc.sellerLocation.address}</Text></View> : null}

              <Text style={od.sec}>Customer</Text>
              <View style={od.kv}><Text style={od.k}>Name</Text><Text style={od.v}>{orderDoc.userLocation?.name || orderDoc.deliveryLocation?.name || '—'}</Text></View>
              <View style={od.kv}><Text style={od.k}>Contact</Text><Text style={od.v}>{orderDoc.userLocation?.contact || orderDoc.userLocation?.secondaryContact || orderDoc.deliveryLocation?.contact || '—'}</Text></View>
              <View style={od.kv}><Text style={od.k}>Drop</Text><Text style={od.v}>{orderDoc.userLocation?.address || orderDoc.deliveryLocation?.address || '—'}</Text></View>

              <Text style={od.sec}>Delivery Partner</Text>
              <View style={od.kv}><Text style={od.k}>Name</Text><Text style={od.v}>{assignedPartner?.name || orderDoc.deliveryPartnerId || '—'}</Text></View>
              {orderDoc.deliveryPartnerStatus ? <View style={od.kv}><Text style={od.k}>Status</Text><Text style={od.v}>{orderDoc.deliveryPartnerStatus}</Text></View> : null}

              <Text style={od.sec}>Items ({orderItems.length})</Text>
              {orderItems.length === 0
                ? <Text style={od.empty}>No items</Text>
                : orderItems.map((it, idx) => {
                    const nm = it.productName || it.name || 'Item';
                    const qty = it.quantity || it.count || 1;
                    const pr = Number(it.price ?? it.unitPrice ?? 0);
                    return (
                      <View key={idx} style={od.itemRow}>
                        {typeof it.isVeg === 'boolean' ? (
                          <View style={[od.vegDot, { borderColor: it.isVeg ? '#16A34A' : '#DC2626' }]}>
                            <View style={[od.vegDotInner, { backgroundColor: it.isVeg ? '#16A34A' : '#DC2626' }]} />
                          </View>
                        ) : null}
                        <Text style={od.itemName} numberOfLines={1}>{nm}</Text>
                        <Text style={od.itemQty}>{qty} × ₹{pr.toFixed(0)}</Text>
                        <Text style={od.itemTot}>₹{(qty * pr).toFixed(0)}</Text>
                      </View>
                    );
                  })}

              <Text style={od.sec}>Amounts</Text>
              {([['Product', orderDoc.totalPrice], ['Delivery', orderDoc.charges?.deliveryCharge], ['Packing', orderDoc.charges?.packing], ['Surge', orderDoc.charges?.surge], ['Platform', orderDoc.charges?.platformFee]] as [string, any][]).map(([lbl, val]) => (
                <View key={lbl} style={od.kv}><Text style={od.k}>{lbl}</Text><Text style={od.v}>{money(val)}</Text></View>
              ))}
              <View style={od.totalRow}><Text style={od.totalK}>Collected</Text><Text style={od.totalV}>{money(orderDoc.customerPaid)}</Text></View>
              <View style={{ height: 30 }} />
            </ScrollView>
          )}

          {orderToast ? (
            <View style={od.toast}><Text style={od.toastTxt}>{orderToast}</Text></View>
          ) : null}
        </Animated.View>
      </View>
    </Modal>
  );

  return (
    <View style={st.container}>
      {isMobile
        ? (selected ? RoomPane : SessionsPane)
        : <View style={st.split}>{SessionsPane}{RoomPane}</View>}
      {OrderDrawer}
      <RestaurantOrderMapModal visible={mapOpen} order={orderDoc as any} onClose={() => setMapOpen(false)} />
    </View>
  );
}

function Bubble({ m }: { m: ChatMessage }) {
  if (m.messageType === 'system' || m.senderType === 'system') {
    return (
      <View style={st.systemWrap}>
        <View style={st.systemPill}><Text style={st.systemTxt}>{m.text}</Text></View>
      </View>
    );
  }
  const isMe = m.senderType === 'support';
  return (
    <View style={{ alignItems: isMe ? 'flex-end' : 'flex-start' }}>
      <View style={[st.bubble, isMe ? st.bubbleMe : st.bubbleThem]}>
        {m.imageUrl ? <Image source={{ uri: m.imageUrl }} style={st.bubbleImg} /> : null}
        {m.text ? <Text style={[st.bubbleTxt, isMe && { color: '#fff' }]}>{m.text}</Text> : null}
      </View>
      <View style={st.bubbleMeta}>
        <Text style={st.bubbleMetaTxt}>{isMe ? 'You' : (m.senderName || 'Customer')} · {fmtTime(m.timestamp)}</Text>
        {isMe ? <Text style={[st.tick, { color: m.read ? C.blue : C.muted }]}>✓✓</Text> : null}
      </View>
    </View>
  );
}

const st = {
  container: { flex: 1, backgroundColor: C.bg } as any,
  split: { flex: 1, flexDirection: 'row' } as any,

  listPane: { flex: 1, backgroundColor: C.bg } as any,
  tabsRow: { flexDirection: 'row', gap: 6, padding: 10, paddingBottom: 6 } as any,
  tab: { flex: 1, paddingVertical: 8, borderRadius: 8, backgroundColor: C.card, borderWidth: 1, borderColor: C.border, alignItems: 'center' } as any,
  tabActive: { backgroundColor: C.accent, borderColor: C.accent } as any,
  tabTxt: { fontSize: 12, fontWeight: '700', color: C.sub } as any,
  tabTxtActive: { color: '#fff' } as any,

  searchBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 10, borderWidth: 1, borderColor: C.border, paddingHorizontal: 12, marginHorizontal: 10, height: 40 } as any,
  searchInput: { flex: 1, fontSize: 13, color: C.text, outlineStyle: 'none' } as any,

  sessionCard: { backgroundColor: C.card, borderRadius: 14, borderWidth: 1, borderColor: C.border, padding: 12 } as any,
  sessionCardWaiting: { borderWidth: 1.5, borderColor: '#FDE68A' } as any,
  sessionCardSelected: { borderColor: C.accent } as any,
  sessionRow: { flexDirection: 'row', gap: 10 } as any,
  avatar: { width: 44, height: 44, borderRadius: 22, justifyContent: 'center', alignItems: 'center' } as any,
  sessionTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' } as any,
  sessionName: { fontSize: 14, fontWeight: '800', color: C.text, flex: 1 } as any,
  sessionTime: { fontSize: 11, color: C.muted, marginLeft: 6 } as any,
  sessionMeta: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' } as any,
  orderBadge: { backgroundColor: C.blueBg, borderColor: C.blueBorder, borderWidth: 1, borderRadius: 6, paddingHorizontal: 6, paddingVertical: 1 } as any,
  orderBadgeTxt: { color: C.blue, fontSize: 9, fontWeight: '700' } as any,
  dot: { width: 9, height: 9, borderRadius: 5, backgroundColor: '#EA580C' } as any,
  sessionPreview: { fontSize: 12, color: C.sub, marginTop: 4 } as any,

  empty: { alignItems: 'center', paddingTop: 50, gap: 8 } as any,
  emptyTxt: { color: C.sub, fontSize: 13 } as any,

  roomPane: { flex: 1, backgroundColor: C.bg } as any,
  roomEmpty: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 10 } as any,
  roomEmptyTxt: { color: C.sub, fontSize: 14 } as any,
  roomHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, backgroundColor: C.card, borderBottomWidth: 1, borderBottomColor: C.border } as any,
  backBtn: { width: 30, height: 30, justifyContent: 'center', alignItems: 'center' } as any,
  backTxt: { fontSize: 26, color: C.accent, fontWeight: '700' } as any,
  roomName: { fontSize: 15, fontWeight: '800', color: C.text } as any,
  roomEmail: { fontSize: 12, color: C.sub, marginTop: 1 } as any,
  closeBtn: { backgroundColor: '#FEE2E2', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 7 } as any,
  closeBtnTxt: { color: C.danger, fontWeight: '700', fontSize: 12 } as any,

  orderBanner: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.blueBg, borderBottomWidth: 1, borderBottomColor: C.blueBorder, paddingHorizontal: 14, paddingVertical: 8 } as any,

  bubble: { maxWidth: '72%', paddingHorizontal: 14, paddingVertical: 10 } as any,
  bubbleMe: { backgroundColor: C.accent, borderTopLeftRadius: 14, borderTopRightRadius: 14, borderBottomLeftRadius: 14, borderBottomRightRadius: 2 } as any,
  bubbleThem: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderTopLeftRadius: 14, borderTopRightRadius: 14, borderBottomRightRadius: 14, borderBottomLeftRadius: 2 } as any,
  bubbleTxt: { fontSize: 14, color: C.text } as any,
  bubbleImg: { width: 180, height: 140, borderRadius: 10, marginBottom: 6, backgroundColor: C.slateBg } as any,
  bubbleMeta: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 3 } as any,
  bubbleMetaTxt: { fontSize: 9.5, color: C.muted } as any,
  tick: { fontSize: 9.5 } as any,

  systemWrap: { alignItems: 'center', marginVertical: 4 } as any,
  systemPill: { backgroundColor: C.slateBg, borderRadius: 12, paddingHorizontal: 12, paddingVertical: 5 } as any,
  systemTxt: { fontSize: 10.5, color: C.slate } as any,

  closedBar: { padding: 16, alignItems: 'center', backgroundColor: C.card, borderTopWidth: 1, borderTopColor: C.border } as any,
  closedTxt: { color: C.sub, fontSize: 13 } as any,
  inputBar: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, padding: 10, backgroundColor: C.card, borderTopWidth: 1, borderTopColor: C.border } as any,
  input: { flex: 1, backgroundColor: '#F3F4F6', borderRadius: 22, borderWidth: 1, borderColor: C.border, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: C.text, maxHeight: 120, outlineStyle: 'none' } as any,
  sendBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: C.accent, justifyContent: 'center', alignItems: 'center' } as any,
  sendTxt: { color: '#fff', fontSize: 16 } as any,
};

// HTML <select> styling for the partner picker
const orderSelectStyle: React.CSSProperties = {
  flex: 1, height: 42, border: '1px solid #e2e8f0', borderRadius: 8,
  paddingLeft: 10, fontSize: 13, color: '#1a2332', backgroundColor: '#fff', cursor: 'pointer', outline: 'none',
};

// ── Order detail + actions drawer styles (mirrors restaurantOrderScreen od/sd) ──
const od = {
  root: { flex: 1, flexDirection: 'row', justifyContent: 'flex-end' } as any,
  backdrop: { ...({ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } as any), backgroundColor: 'rgba(15,23,42,0.45)' } as any,
  drawer: { height: '100%', backgroundColor: '#f6f8fb', shadowColor: '#000', shadowOffset: { width: -4, height: 0 }, shadowOpacity: 0.18, shadowRadius: 20, elevation: 16 } as any,
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: '#e2e8f0', gap: 12 } as any,
  closeBtn: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center' } as any,
  closeTxt: { fontSize: 14, color: '#64748b', fontWeight: '600' } as any,
  title: { flex: 1, fontSize: 17, fontWeight: '700', color: '#1a2332', textAlign: 'center' } as any,
  loadingWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 30 } as any,

  topRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 } as any,
  statusPill: { flexDirection: 'row', alignItems: 'center', borderRadius: 14, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 4 } as any,
  statusDot: { width: 7, height: 7, borderRadius: 4, marginRight: 6 } as any,
  statusPillTxt: { fontSize: 11, fontWeight: '800' } as any,

  sec: { fontSize: 11, fontWeight: '800', color: '#64748b', letterSpacing: 0.6, textTransform: 'uppercase', marginTop: 18, marginBottom: 8, borderBottomWidth: 1, borderBottomColor: '#eef2f6', paddingBottom: 5 } as any,
  mapBtn: { marginTop: 14, paddingVertical: 12, borderRadius: 10, alignItems: 'center', backgroundColor: '#EFF6FF', borderWidth: 1, borderColor: '#BFDBFE' } as any,
  mapTxt: { color: '#2563EB', fontWeight: '700', fontSize: 13 } as any,
  kv: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 4 } as any,
  k: { fontSize: 12, color: '#94a3b8', fontWeight: '600' } as any,
  v: { fontSize: 13, color: '#1a2332', flexShrink: 1, textAlign: 'right', fontWeight: '500' } as any,
  empty: { fontSize: 12, color: '#94a3b8', fontStyle: 'italic' } as any,

  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 } as any,
  chip: { paddingHorizontal: 11, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: '#e2e8f0', backgroundColor: '#fff' } as any,
  chipTxt: { fontSize: 11, fontWeight: '700', color: '#64748b' } as any,

  quickRow: { flexDirection: 'row', gap: 8, marginTop: 12 } as any,
  quickBtn: { flex: 1, paddingVertical: 11, borderRadius: 9, alignItems: 'center' } as any,
  quickTxt: { color: '#fff', fontWeight: '700', fontSize: 12 } as any,
  cancelBtn: { marginTop: 8, paddingVertical: 11, borderRadius: 9, alignItems: 'center', backgroundColor: '#FEE2E2', borderWidth: 1, borderColor: '#FCA5A5' } as any,
  cancelTxt: { color: '#DC2626', fontWeight: '800', fontSize: 12 } as any,

  row2: { flexDirection: 'row', alignItems: 'center', gap: 8 } as any,
  input: { flex: 1, borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 13, color: '#1a2332', backgroundColor: '#fff', outlineStyle: 'none' } as any,
  saveBtn: { backgroundColor: '#21808D', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 11, alignItems: 'center' } as any,
  saveTxt: { color: '#fff', fontWeight: '700', fontSize: 12 } as any,

  itemRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' } as any,
  itemName: { flex: 1, fontSize: 13, color: '#1a2332' } as any,
  itemQty: { fontSize: 12, color: '#64748b' } as any,
  itemTot: { fontSize: 13, fontWeight: '700', color: '#1a2332', minWidth: 56, textAlign: 'right' } as any,
  vegDot: { width: 14, height: 14, borderRadius: 3, borderWidth: 1.5, justifyContent: 'center', alignItems: 'center' } as any,
  vegDotInner: { width: 6, height: 6, borderRadius: 3 } as any,

  totalRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#e2e8f0' } as any,
  totalK: { fontSize: 14, fontWeight: '800', color: '#1a2332' } as any,
  totalV: { fontSize: 16, fontWeight: '800', color: '#10B981' } as any,

  toast: { position: 'absolute', bottom: 18, left: 16, right: 16, backgroundColor: '#1a2332', borderRadius: 10, paddingVertical: 11, paddingHorizontal: 14, alignItems: 'center' } as any,
  toastTxt: { color: '#fff', fontSize: 13, fontWeight: '600' } as any,
};
